import React, { useState, useEffect } from 'react';
import { TEAM_ACCESS_PERMISSIONS, getDefaultTeamAccess } from '../constants/teamAccessPermissions';
import { adminUserService } from '../../services/adminUserService';
import { validateImageFile } from '../../utils/fileUtils';
import { uploadCreatorBannerImage } from '../utils/uploadAdminMedia';
import {
  readCreatorStorefrontFromStorage,
} from '../../utils/creatorStorefront';
import './UserEditModal.css';

const UserEditModal = ({ user, isOpen, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    role: 'user',
    isActive: true,
    addresses: [],
    newPassword: '',
    confirmPassword: ''
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [teamAccess, setTeamAccess] = useState(() => getDefaultTeamAccess());
  const [passwordSectionExpanded, setPasswordSectionExpanded] = useState(false);
  const [activeTab, setActiveTab] = useState('profile');
  const [creatorStorefront, setCreatorStorefront] = useState({
    bannerUrl: '',
    headline: '',
    bio: '',
  });
  const [creatorBannerUploading, setCreatorBannerUploading] = useState(false);
  const [creatorLoadError, setCreatorLoadError] = useState('');

  const hydrateCreatorFromUserLike = (u, uid) => {
    const stored = uid ? readCreatorStorefrontFromStorage(uid) : null;
    setCreatorStorefront({
      bannerUrl: u?.creatorBannerUrl || stored?.bannerUrl || '',
      headline: u?.creatorHeadline || stored?.headline || '',
      bio: u?.creatorBio || u?.bio || stored?.bio || '',
    });
  };

  // Initialize form data when user changes
  useEffect(() => {
    if (user) {
      setFormData({
        name: user.name || '',
        email: user.email || '',
        phone: user.phone || '',
        role: user.role || 'user',
        isActive: user.isActive !== undefined ? user.isActive : true,
        addresses: user.addresses || [],
        newPassword: '',
        confirmPassword: ''
      });
      setErrors({});
      setTeamAccess(user.accessPermissions ? { ...getDefaultTeamAccess(), ...user.accessPermissions } : getDefaultTeamAccess());
      setPasswordSectionExpanded(false);
      setActiveTab('profile');
      setCreatorLoadError('');
      const uid = user._id || user.id;
      hydrateCreatorFromUserLike(user, uid);
      if (user.role === 'team' && uid) {
        adminUserService
          .getUser(uid)
          .then((res) => {
            if (res?.success && res.data) {
              hydrateCreatorFromUserLike(res.data, uid);
            }
          })
          .catch(() => {
            setCreatorLoadError('Could not refresh shop header from server; showing saved/local values.');
          });
      }
    }
  }, [user]);

  // Toggle team access permission
  const toggleTeamAccess = (category, label) => {
    const key = `${category}|${label}`;
    setTeamAccess(prev => ({ ...prev, [key]: !prev[key] }));
  };

  // Handle input changes
  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }
    
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    
    if (formData.phone && !/^[\+]?[1-9][\d]{0,15}$/.test(formData.phone.replace(/\s/g, ''))) {
      newErrors.phone = 'Please enter a valid phone number';
    }
    
    // Password update validation (optional - only when filled)
    if (formData.newPassword || formData.confirmPassword) {
      if (!formData.newPassword) {
        newErrors.newPassword = 'New password is required';
      } else if (formData.newPassword.length < 6) {
        newErrors.newPassword = 'Password must be at least 6 characters';
      } else if (formData.newPassword !== formData.confirmPassword) {
        newErrors.confirmPassword = 'Passwords do not match';
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setLoading(true);
    
    try {
      // Exclude password fields from main user data - onSave handles password separately
      const { newPassword, confirmPassword, ...userData } = formData;
      const payload = newPassword ? { ...userData, newPassword } : userData;
      if (formData.role === 'team') {
        payload.accessPermissions = teamAccess;
        payload.creatorBannerUrl = creatorStorefront.bannerUrl?.trim() || '';
        payload.creatorHeadline = creatorStorefront.headline?.trim() || '';
        payload.creatorBio = creatorStorefront.bio?.trim() || '';
      }
      console.log('💾 Submitting user edit form:', { ...userData, hasPasswordUpdate: !!newPassword, hasAccessUpdate: formData.role === 'team' });
      await onSave(payload);
    } catch (error) {
      console.error('❌ Error submitting form:', error);
    } finally {
      setLoading(false);
    }
  };

  // Handle address changes
  const handleAddressChange = (index, field, value) => {
    const newAddresses = [...formData.addresses];
    newAddresses[index] = { ...newAddresses[index], [field]: value };
    setFormData(prev => ({ ...prev, addresses: newAddresses }));
  };

  // Add new address
  const addAddress = () => {
    setFormData(prev => ({
      ...prev,
      addresses: [...prev.addresses, {
        type: 'home',
        street: '',
        city: '',
        state: '',
        zipCode: '',
        country: 'USA',
        isDefault: prev.addresses.length === 0
      }]
    }));
  };

  // Remove address
  const removeAddress = (index) => {
    const newAddresses = formData.addresses.filter((_, i) => i !== index);
    setFormData(prev => ({ ...prev, addresses: newAddresses }));
  };

  // When role changes to team, ensure teamAccess has defaults
  const handleRoleChange = (value) => {
    handleInputChange('role', value);
    if (value === 'team') {
      setTeamAccess(getDefaultTeamAccess());
    } else {
      setActiveTab('profile');
    }
  };

  const handleCreatorBannerFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    const v = validateImageFile(file, 5);
    if (!v.isValid) {
      setErrors((prev) => ({ ...prev, creatorBanner: v.errors.join(' ') }));
      return;
    }
    try {
      setCreatorBannerUploading(true);
      setErrors((prev) => ({ ...prev, creatorBanner: '' }));
      const { url } = await uploadCreatorBannerImage(file);
      setCreatorStorefront((prev) => ({ ...prev, bannerUrl: url }));
    } catch (err) {
      setErrors((prev) => ({
        ...prev,
        creatorBanner: err?.message || 'Banner upload failed',
      }));
    } finally {
      setCreatorBannerUploading(false);
    }
  };

  // Set default address
  const setDefaultAddress = (index) => {
    const newAddresses = formData.addresses.map((addr, i) => ({
      ...addr,
      isDefault: i === index
    }));
    setFormData(prev => ({ ...prev, addresses: newAddresses }));
  };

  if (!isOpen || !user) return null;

  const isTeamMember = formData.role === 'team';

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal-content user-edit-modal user-edit-modal-content"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header">
          <h3>Edit User: {user.name}</h3>
          <button type="button" onClick={onClose} className="close-btn">
            ✕
          </button>
        </div>

        {isTeamMember && (
          <div className="user-edit-modal-tabs" role="tablist" aria-label="Edit user sections">
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === 'profile'}
              className={`user-edit-modal-tab${activeTab === 'profile' ? ' is-active' : ''}`}
              onClick={() => setActiveTab('profile')}
            >
              Profile &amp; access
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === 'creator'}
              className={`user-edit-modal-tab${activeTab === 'creator' ? ' is-active' : ''}`}
              onClick={() => setActiveTab('creator')}
            >
              Creator page (Shop header)
            </button>
          </div>
        )}

        <form onSubmit={handleSubmit} className="user-edit-form">
          {activeTab === 'profile' && (
          <>
          <div className="form-section">
            <h4>Basic Information</h4>
            
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="name">Name *</label>
                <input
                  type="text"
                  id="name"
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  className={errors.name ? 'error' : ''}
                  placeholder="Enter full name"
                />
                {errors.name && <span className="error-text">{errors.name}</span>}
              </div>
              
              <div className="form-group">
                <label htmlFor="email">Email *</label>
                <input
                  type="email"
                  id="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  className={errors.email ? 'error' : ''}
                  placeholder="Enter email address"
                />
                {errors.email && <span className="error-text">{errors.email}</span>}
              </div>
            </div>
            
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="phone">Phone</label>
                <input
                  type="tel"
                  id="phone"
                  value={formData.phone}
                  onChange={(e) => handleInputChange('phone', e.target.value)}
                  className={errors.phone ? 'error' : ''}
                  placeholder="Enter phone number"
                />
                {errors.phone && <span className="error-text">{errors.phone}</span>}
              </div>
              
              <div className="form-group">
                <label htmlFor="role">Role</label>
                <select
                  id="role"
                  value={formData.role}
                  onChange={(e) => handleRoleChange(e.target.value)}
                >
                  <option value="user">User</option>
                  <option value="admin">Admin</option>
                  <option value="team">Team Member</option>
                </select>
              </div>
            </div>
            
            <div className="form-group checkbox-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={formData.isActive}
                  onChange={(e) => handleInputChange('isActive', e.target.checked)}
                />
                <span className="checkbox-text">Active Account</span>
              </label>
            </div>

            {/* Team Member Access Matrix - shown when role is team */}
            {formData.role === 'team' && (
              <div className="form-section access-permissions-section">
                <h4>Team Member Access</h4>
                <p className="access-description">Toggle access permissions for this team member:</p>
                <div className="access-categories">
                  {TEAM_ACCESS_PERMISSIONS.map((perm) => (
                    <div key={perm.category} className="access-category">
                      <div className="access-category-name">{perm.category}</div>
                      <div className="access-buttons">
                        {perm.items.map((item) => {
                          const key = `${perm.category}|${item.label}`;
                          const isOn = teamAccess[key] ?? item.defaultOn;
                          return (
                            <button
                              key={key}
                              type="button"
                              className={`access-btn access-toggle ${isOn ? 'access-on' : 'access-off'}`}
                              onClick={() => toggleTeamAccess(perm.category, item.label)}
                              title={isOn ? 'ON - Click to turn OFF' : 'OFF - Click to turn ON'}
                            >
                              {isOn ? '✓' : '✗'} {item.label}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="form-section password-update-section">
              <button
                type="button"
                className="password-update-toggle"
                onClick={() => setPasswordSectionExpanded(!passwordSectionExpanded)}
              >
                <h4>Update Password (Optional)</h4>
                <i className={`fas fa-chevron-${passwordSectionExpanded ? 'up' : 'down'}`}></i>
              </button>
              {passwordSectionExpanded && (
                <>
                  <p className="password-update-hint">Leave blank to keep current password</p>
                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="newPassword">Enter Password</label>
                      <input
                        type="password"
                        id="newPassword"
                        value={formData.newPassword}
                        onChange={(e) => handleInputChange('newPassword', e.target.value)}
                        className={errors.newPassword ? 'error' : ''}
                        placeholder="Enter new password"
                      />
                      {errors.newPassword && <span className="error-text">{errors.newPassword}</span>}
                    </div>
                    <div className="form-group">
                      <label htmlFor="confirmPassword">Re-enter Password</label>
                      <input
                        type="password"
                        id="confirmPassword"
                        value={formData.confirmPassword}
                        onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                        className={errors.confirmPassword ? 'error' : ''}
                        placeholder="Re-enter new password"
                      />
                      {errors.confirmPassword && <span className="error-text">{errors.confirmPassword}</span>}
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>

          <div className="form-section">
            <div className="section-header">
              <h4>Addresses</h4>
              <button type="button" onClick={addAddress} className="add-address-btn">
                + Add Address
              </button>
            </div>
            
            {formData.addresses.map((address, index) => (
              <div key={index} className="address-card">
                <div className="address-header">
                  <select
                    value={address.type}
                    onChange={(e) => handleAddressChange(index, 'type', e.target.value)}
                    className="address-type"
                  >
                    <option value="home">Home</option>
                    <option value="work">Work</option>
                    <option value="other">Other</option>
                  </select>
                  
                  <div className="address-actions">
                    <label className="default-checkbox">
                      <input
                        type="checkbox"
                        checked={address.isDefault}
                        onChange={() => setDefaultAddress(index)}
                      />
                      Default
                    </label>
                    
                    {formData.addresses.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeAddress(index)}
                        className="remove-address-btn"
                      >
                        Remove
                      </button>
                    )}
                  </div>
                </div>
                
                <div className="address-fields">
                  <div className="form-group">
                    <label>Street</label>
                    <input
                      type="text"
                      value={address.street}
                      onChange={(e) => handleAddressChange(index, 'street', e.target.value)}
                      placeholder="Street address"
                    />
                  </div>
                  
                  <div className="form-row">
                    <div className="form-group">
                      <label>City</label>
                      <input
                        type="text"
                        value={address.city}
                        onChange={(e) => handleAddressChange(index, 'city', e.target.value)}
                        placeholder="City"
                      />
                    </div>
                    
                    <div className="form-group">
                      <label>State</label>
                      <input
                        type="text"
                        value={address.state}
                        onChange={(e) => handleAddressChange(index, 'state', e.target.value)}
                        placeholder="State"
                      />
                    </div>
                    
                    <div className="form-group">
                      <label>ZIP Code</label>
                      <input
                        type="text"
                        value={address.zipCode}
                        onChange={(e) => handleAddressChange(index, 'zipCode', e.target.value)}
                        placeholder="ZIP Code"
                      />
                    </div>
                  </div>
                  
                  <div className="form-group">
                    <label>Country</label>
                    <select
                      value={address.country}
                      onChange={(e) => handleAddressChange(index, 'country', e.target.value)}
                    >
                      <option value="USA">United States</option>
                      <option value="Canada">Canada</option>
                      <option value="UK">United Kingdom</option>
                      <option value="Australia">Australia</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                </div>
              </div>
            ))}
          </div>
          </>
          )}

          {activeTab === 'creator' && isTeamMember && (
            <div className="form-section user-edit-creator-section">
              <h4>Creator page (Shop header)</h4>
              <p className="user-edit-creator-hint">
                Public shop header when customers view this team member&apos;s creator page. Existing
                values load automatically; change and save with the button below.
              </p>
              {creatorLoadError && (
                <p className="user-edit-creator-warn" role="status">
                  {creatorLoadError}
                </p>
              )}

              <div className="form-group">
                <label>Shop banner image</label>
                {creatorStorefront.bannerUrl ? (
                  <div className="user-edit-creator-banner-preview-wrap">
                    <img
                      src={creatorStorefront.bannerUrl}
                      alt="Shop banner preview"
                      className="user-edit-creator-banner-preview"
                    />
                    <button
                      type="button"
                      className="user-edit-creator-remove-banner"
                      onClick={() =>
                        setCreatorStorefront((prev) => ({ ...prev, bannerUrl: '' }))
                      }
                    >
                      Remove banner
                    </button>
                  </div>
                ) : (
                  <p className="user-edit-field-hint">No banner yet. Upload a wide image (e.g. 1200×400 px).</p>
                )}
                <label className="user-edit-upload-label">
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp,image/gif"
                    onChange={handleCreatorBannerFile}
                    disabled={creatorBannerUploading}
                  />
                  <span className="user-edit-upload-btn">
                    <i
                      className={`fas ${creatorBannerUploading ? 'fa-spinner fa-spin' : 'fa-upload'}`}
                      aria-hidden
                    />{' '}
                    {creatorBannerUploading ? 'Uploading…' : 'Upload / change banner'}
                  </span>
                </label>
                {errors.creatorBanner && (
                  <span className="error-text">{errors.creatorBanner}</span>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="editCreatorHeadline">Headline</label>
                <input
                  id="editCreatorHeadline"
                  type="text"
                  value={creatorStorefront.headline ?? ''}
                  onChange={(e) =>
                    setCreatorStorefront((prev) => ({ ...prev, headline: e.target.value }))
                  }
                  placeholder="e.g. Handpicked styles by this shop"
                />
              </div>

              <div className="form-group">
                <label htmlFor="editCreatorBio">Short bio</label>
                <textarea
                  id="editCreatorBio"
                  rows={4}
                  value={creatorStorefront.bio ?? ''}
                  onChange={(e) =>
                    setCreatorStorefront((prev) => ({ ...prev, bio: e.target.value }))
                  }
                  placeholder="Tell customers about this collection…"
                />
              </div>
            </div>
          )}

          <div className="modal-actions">
            <button type="button" onClick={onClose} className="cancel-btn">
              Cancel
            </button>
            <button 
              type="submit" 
              className="save-btn"
              disabled={loading}
            >
              {loading ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UserEditModal;

